<template>
  <!--
    Video Block that displays a thumbnail image, title and description.
    When the thumbnail image or "watch now" is clicked an iframe video will expand,
    hiding the description and showing a collapsible transcript.
  -->
  <div
    class="video-block"
    :class="{'video-block--expanded': showVideo}"
  >
    <!-- Thumbnail Image -->
    <button
      v-show="!showVideo"
      title="watch now"
      class="video-block__thumbnail"
      @click="toggleVideo"
      @keyup.space="toggleVideo"
    >
      <div class="video-block__thumbnail-container">
        <div
          class="video-block__thumbnail-wrapper"
        >
          <slot name="thumbnail" class="video-block__thumbnail-image"/>
          <div class="video-block__thumbnail-icon-wrapper">
            <font-awesome-icon
              class="video-block__thumbnail-icon"
              icon="play"
            />
          </div>
        </div>
      </div>
    </button>
    <!-- Video -->
    <div
      v-show="showVideo"
      class="video-block__player"
    >
      <div class="video-block__player-wrapper">
        <pulse-loader
          :color="'#E4E6E2'"
          :size="'20px'"
          :margin="'10px'"
        />
        <iframe
          v-if="showVideo"
          :src="computedUrl"
          :title="title"
          class="video-block__iframe"
          frameborder="0"
          allow="autoplay; fullscreen"
          allowfullscreen
        />
      </div>
      <!-- Transcript -->
      <div class="video-block__transcript">
        <button
          :id="`transcript-button-${videoID}`"
          class="video-block__transcript-button"
          :class="{'video-block__transcript-button--expanded': showTranscript}"
          :aria-controls="`transcript-content-${videoID}`"
          :aria-label="`Video transcript`"
          :aria-expanded="showTranscript"
          @click="toggleTranscript"
          @keyup.space="toggleTranscript"
        >
          <span class="video-block__transcript-label">
            <img
              class="video-block__transcript-icon"
              :src="`/themes/custom/te_rito_theme/dist/images/icon-transcript.svg`"
              alt=""
            >
            <span>
              Video transcript
            </span>
          </span>
          <img
            class="video-block__transcript-close-btn"
            :src="`/themes/custom/te_rito_theme/dist/images/icon-close.svg`"
            alt="Close transcript icon"
          >
        </button>
        <div
          :id="`transcript-content-${videoID}`"
          class="video-block__transcript-content"
          :class="{'video-block__transcript-content--expanded': showTranscript}"
          :aria-labelledby="`transcript-button-${videoID}`"
          :aria-hidden="!showTranscript"
        >
          <div
            class="video-block__transcript-content-inner"
            :class="{'video-block__transcript-content-inner--expanded': showTranscript}"
          >
            <slot name="transcript" />
          </div>
        </div>
      </div>
    </div>
    <!-- Title & Description -->
    <div
      class="video-block__content"
      :class="{'video-block__content--expanded': showVideo}"
    >
      <div class="video-block__title-wrapper">
        <h3
          class="video-block__title"
          :class="{'video-block__title--expanded': showVideo}"
          @click="toggleVideo"
          @keyup.space="toggleVideo"
        >
          {{ title }}
        </h3>
      </div>
      <button
        v-if="showVideo && !showLargeOnly"
        class="video-block__close c-button c-button--link"
        @click="toggleVideo"
        @keyup.space="toggleVideo"
      >
        <img
          :src="`/themes/custom/te_rito_theme/dist/images/icon-close.svg`"
          alt="Close video icon"
        >
      </button>
      <div
        v-if="!showVideo"
        class="video-block__description"
      >
        {{ description }}
      </div>
      <div class="video-block__content-footer">
        <!-- eslint-disable-next-line max-len -->
        <button
          v-if="!showVideo"
          class="
            video-block__watch-button
            c-button
            c-button--link
            c-button--bold
            c-button--uppercase
          "
          @click="toggleVideo"
          @keyup.space="toggleVideo"
        >
          watch now
        </button>
        <span class="video-block__duration">
          {{ duration }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { PulseLoader } from 'vue-spinner/dist/vue-spinner.min';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  name: 'VideoBlock',
  components: {
    PulseLoader,
    FontAwesomeIcon,
  },
  props: {
    /**
     * Title for the block
     */
    title: {
      type: String,
      required: true,
    },
    /**
     * Description for the block
     */
    description: {
      type: String,
      required: true,
    },
    /**
     * Video duration
     */
    duration: {
      type: String,
      required: true,
    },
    /**
     * Alternative text for the thumbnail image
     */
    thumbnailAlt: {
      type: String,
      required: false,
      default: '',
    },
    /**
    * If true, only the large expanded view is displayed
    */
    isLargeView: {
      type: String,
      required: false,
      default: 'Off',
    },
    /**
     * Video image url from drupal. Needs to be parsed to extract ID
     */
    vimeoVideo: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      showLargeOnly: this.isLargeView === 'On',
      showVideo: false,
      showTranscript: false,
      imageLoaded: false,
      videoID: 0,
    };
  },
  async mounted() {
    this.showVideo = this.showLargeOnly; 
    const thumbnailImage = /[^/]*$/.exec(this.vimeoVideo)[0];
    this.videoID = thumbnailImage.split('.')[0];
    this.imageLoaded=true;
  },
  computed: {
    /**
    * Do not autoplay video when large view only is enabled
    */
    computedUrl(){
      return this.showLargeOnly ? this.videoUrl : this.videoUrl + `?autoplay=1`;
    },
    videoUrl(){
      return `https://player.vimeo.com/video/${this.videoID}`;
    }
  },
  methods: {
    /**
     * Used for hiding and showing the video iframe
     */
    toggleVideo() {
      if (this.showLargeOnly) return;
      this.showVideo = !this.showVideo;
    },
    /**
     * Used for expanding and collapsing the video transcript
     */
    toggleTranscript() {
      this.showTranscript = !this.showTranscript;
    },
  },
};
</script>